import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  NICKNAME_MAX_LENGTH,
  NICKNAME_MIN_LENGTH,
  ROOM_CODE_ALPHABET,
  ROOM_CODE_LENGTH,
  type SessionCreated,
} from '@sincronia/shared';
import { Button, Field, Panel } from '../components/ui.js';
import { BrandMark } from '../components/Brand.js';
import { errorKey, useT } from '../i18n/index.js';
import { emitAck, getSocket } from '../net/socket.js';
import { lastUsedName, saveSession } from '../lib/session.js';
import { useGameStore } from '../store/gameStore.js';
import { unlockAudio } from '../lib/feedback.js';

const CODE_PATTERN = new RegExp(`^[${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}}$`);

export function normalizeCode(value: string): string {
  return value
    .toUpperCase()
    .split('')
    .filter((character) => ROOM_CODE_ALPHABET.includes(character))
    .join('')
    .slice(0, ROOM_CODE_LENGTH);
}

export function JoinPage(): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setSession = useGameStore((state) => state.setSession);

  const [code, setCode] = useState(() => normalizeCode(params.get('codigo') ?? ''));
  const [name, setName] = useState(() => lastUsedName());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getSocket();
  }, []);

  const trimmed = name.trim();
  const nameValid = trimmed.length >= NICKNAME_MIN_LENGTH && trimmed.length <= NICKNAME_MAX_LENGTH;
  const codeValid = CODE_PATTERN.test(code);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!nameValid || !codeValid || busy) return;

    unlockAudio();
    setBusy(true);
    setError(null);

    const response = await emitAck<SessionCreated>('room:join', { code, name: trimmed });
    setBusy(false);

    if (!response.ok) {
      setError(t(errorKey(response.error.code)));
      return;
    }

    saveSession(response.data, trimmed);
    setSession(response.data);
    void navigate(`/sala/${response.data.code}`, { replace: true });
  }

  return (
    <main className="safe-x safe-bottom safe-top mx-auto flex w-full max-w-md flex-1 flex-col gap-6 px-5 py-8">
      <header className="flex items-center gap-3">
        <BrandMark size={40} animated={false} />
        <h1 className="text-2xl font-semibold text-night-200">{t('join.title')}</h1>
      </header>

      <form className="flex flex-col gap-5" onSubmit={(event) => void handleSubmit(event)}>
        <Panel className="flex flex-col gap-5">
          <Field
            label={t('join.code')}
            placeholder={t('join.codePlaceholder')}
            value={code}
            onChange={(event) => setCode(normalizeCode(event.target.value))}
            hint={t('join.codeHint')}
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            className="tabular text-center text-2xl tracking-[0.4em] uppercase"
            data-testid="join-code"
            required
          />
          <Field
            label={t('join.nickname')}
            placeholder={t('create.nicknamePlaceholder')}
            value={name}
            autoComplete="nickname"
            maxLength={NICKNAME_MAX_LENGTH}
            onChange={(event) => setName(event.target.value)}
            data-testid="join-name"
            required
          />
        </Panel>

        {error ? (
          <p role="alert" className="rounded-2xl bg-life/10 p-3 text-sm text-life">
            {error}
          </p>
        ) : null}

        <div className="flex flex-col gap-3">
          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={busy}
            disabled={!nameValid || !codeValid}
            data-testid="join-submit"
          >
            {busy ? t('join.joining') : t('join.submit')}
          </Button>
          <Button variant="ghost" fullWidth onClick={() => void navigate('/')}>
            {t('common.back')}
          </Button>
        </div>
      </form>
    </main>
  );
}
